import * as React from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

type RGB = { r: number; g: number; b: number };
type HSV = { h: number; s: number; v: number };

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

function hexToRgb(hex: string): RGB | null {
  const cleaned = hex.startsWith("#") ? hex.slice(1) : hex;
  if (!/^[0-9a-fA-F]{6}$/.test(cleaned)) return null;
  const r = Number.parseInt(cleaned.slice(0, 2), 16);
  const g = Number.parseInt(cleaned.slice(2, 4), 16);
  const b = Number.parseInt(cleaned.slice(4, 6), 16);
  return { r, g, b };
}

function rgbToHex({ r, g, b }: RGB) {
  const toHex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${toHex(clamp(Math.round(r), 0, 255))}${toHex(clamp(Math.round(g), 0, 255))}${toHex(
    clamp(Math.round(b), 0, 255)
  )}`.toUpperCase();
}

function rgbToHsv({ r, g, b }: RGB): HSV {
  const rr = r / 255;
  const gg = g / 255;
  const bb = b / 255;

  const max = Math.max(rr, gg, bb);
  const min = Math.min(rr, gg, bb);
  const d = max - min;

  let h = 0;
  if (d !== 0) {
    switch (max) {
      case rr:
        h = ((gg - bb) / d) % 6;
        break;
      case gg:
        h = (bb - rr) / d + 2;
        break;
      case bb:
        h = (rr - gg) / d + 4;
        break;
    }
    h *= 60;
    if (h < 0) h += 360;
  }

  const s = max === 0 ? 0 : d / max;
  const v = max;
  return { h, s, v };
}

function hsvToRgb({ h, s, v }: HSV): RGB {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;

  let rr: number, gg: number, bb: number;
  if (h >= 0 && h < 60) {
    rr = c;
    gg = x;
    bb = 0;
  } else if (h >= 60 && h < 120) {
    rr = x;
    gg = c;
    bb = 0;
  } else if (h >= 120 && h < 180) {
    rr = 0;
    gg = c;
    bb = x;
  } else if (h >= 180 && h < 240) {
    rr = 0;
    gg = x;
    bb = c;
  } else if (h >= 240 && h < 300) {
    rr = x;
    gg = 0;
    bb = c;
  } else {
    rr = c;
    gg = 0;
    bb = x;
  }

  return {
    r: (rr + m) * 255,
    g: (gg + m) * 255,
    b: (bb + m) * 255,
  };
}

function hsvHueToCss(h: number) {
  return `hsl(${Math.round(h)} 100% 50%)`;
}

export function HsvColorPicker({
  value,
  onChange,
  className,
  closeButton,
}: {
  value: string;
  onChange: (hex: string) => void;
  className?: string;
  closeButton?: React.ReactNode;
}) {
  const initialHsv = React.useMemo<HSV>(() => {
    const rgb = hexToRgb(value) ?? { r: 59, g: 130, b: 246 };
    return rgbToHsv(rgb);
  }, [value]);

  const [hsv, setHsv] = React.useState<HSV>(initialHsv);
  const [hexInput, setHexInput] = React.useState(value);
  
  React.useEffect(() => {
    setHsv(initialHsv);
    setHexInput(value);
  }, [initialHsv, value]);

  const rgb = React.useMemo(() => hsvToRgb(hsv), [hsv]);
  const hex = React.useMemo(() => rgbToHex(rgb), [rgb]);
  
  // Sync hexInput when hex changes from picker/sliders
  React.useEffect(() => {
    setHexInput(hex);
  }, [hex]);

  const svRef = React.useRef<HTMLDivElement | null>(null);
  const hueRef = React.useRef<HTMLDivElement | null>(null);
  const dragModeRef = React.useRef<"sv" | "hue" | null>(null);

  const commitHsv = React.useCallback(
    (next: HSV) => {
      const safe: HSV = {
        h: clamp(next.h, 0, 360),
        s: clamp(next.s, 0, 1),
        v: clamp(next.v, 0, 1),
      };
      setHsv(safe);
      onChange(rgbToHex(hsvToRgb(safe)));
    },
    [onChange]
  );

  const updateSVFromEvent = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const el = svRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = clamp(e.clientX - rect.left, 0, rect.width);
      const y = clamp(e.clientY - rect.top, 0, rect.height);
      const s = rect.width === 0 ? 0 : x / rect.width;
      const v = rect.height === 0 ? 0 : 1 - y / rect.height;
      commitHsv({ ...hsv, s, v });
    },
    [commitHsv, hsv]
  );

  const updateHueFromEvent = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const el = hueRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = clamp(e.clientX - rect.left, 0, rect.width);
      const h = rect.width === 0 ? 0 : (x / rect.width) * 360;
      commitHsv({ ...hsv, h });
    },
    [commitHsv, hsv]
  );

  const onPointerDownSV = (e: React.PointerEvent<HTMLDivElement>) => {
    dragModeRef.current = "sv";
    e.currentTarget.setPointerCapture(e.pointerId);
    updateSVFromEvent(e);
  };

  const onPointerDownHue = (e: React.PointerEvent<HTMLDivElement>) => {
    dragModeRef.current = "hue";
    e.currentTarget.setPointerCapture(e.pointerId);
    updateHueFromEvent(e);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragModeRef.current === "sv") updateSVFromEvent(e);
    if (dragModeRef.current === "hue") updateHueFromEvent(e);
  };

  const onPointerUp = () => {
    dragModeRef.current = null;
  };

  const setRgbChannel = (channel: keyof RGB, nextValue: number) => {
    const nextRgb: RGB = {
      r: clamp(Math.round(rgb.r), 0, 255),
      g: clamp(Math.round(rgb.g), 0, 255),
      b: clamp(Math.round(rgb.b), 0, 255),
      [channel]: clamp(Math.round(nextValue), 0, 255),
    };
    commitHsv(rgbToHsv(nextRgb));
  };

  const hueCss = hsvHueToCss(hsv.h);
  const svIndicatorLeft = `${hsv.s * 100}%`;
  const svIndicatorTop = `${(1 - hsv.v) * 100}%`;
  const hueIndicatorLeft = `${(hsv.h / 360) * 100}%`;

  return (
    <div className={cn("space-y-5", className)}>
      {/* Saturation / Value */}
      <div
        ref={svRef}
        className="relative w-full aspect-[4/1] rounded-lg overflow-hidden touch-none"
        style={{ backgroundColor: hueCss }}
        onPointerDown={onPointerDownSV}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(to right, #fff, rgba(255,255,255,0))" }}
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(to top, #000, rgba(0,0,0,0))" }}
        />

        <div
          className="absolute h-4 w-4 rounded-full border-2 border-background shadow"
          style={{ left: svIndicatorLeft, top: svIndicatorTop, transform: "translate(-50%, -50%)" }}
        />
      </div>

      {/* Hue slider + preview */}
      <div className="flex items-center gap-3">
        <div
          className="h-8 w-8 rounded-full border"
          style={{ backgroundColor: hex }}
          aria-label="Aperçu de la couleur"
        />

        <div
          ref={hueRef}
          className="relative h-3 flex-1 rounded-full overflow-hidden touch-none"
          style={{
            background:
              "linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)",
          }}
          onPointerDown={onPointerDownHue}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <div
            className="absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full border-2 border-background shadow"
            style={{ left: hueIndicatorLeft, transform: "translate(-50%, -50%)" }}
          />
        </div>
      </div>

      {/* RGB inputs + Hex + Close button */}
      <div className="flex items-center gap-3">
        {closeButton}
        <div className="grid grid-cols-3 gap-2 flex-1">
          <div className="space-y-1">
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              max={255}
              value={clamp(Math.round(rgb.r), 0, 255)}
              onChange={(e) => setRgbChannel("r", Number(e.target.value))}
              className="h-8 text-sm px-2"
            />
            <div className="text-center text-xs text-muted-foreground">R</div>
          </div>
          <div className="space-y-1">
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              max={255}
              value={clamp(Math.round(rgb.g), 0, 255)}
              onChange={(e) => setRgbChannel("g", Number(e.target.value))}
              className="h-8 text-sm px-2"
            />
            <div className="text-center text-xs text-muted-foreground">G</div>
          </div>
          <div className="space-y-1">
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              max={255}
              value={clamp(Math.round(rgb.b), 0, 255)}
              onChange={(e) => setRgbChannel("b", Number(e.target.value))}
              className="h-8 text-sm px-2"
            />
            <div className="text-center text-xs text-muted-foreground">B</div>
          </div>
        </div>
        <div className="space-y-1">
          <Input
            type="text"
            value={hexInput}
            onChange={(e) => {
              let val = e.target.value.toUpperCase();
              if (!val.startsWith('#')) val = '#' + val;
              val = val.slice(0, 7);
              setHexInput(val);
              const rgb = hexToRgb(val);
              if (rgb) commitHsv(rgbToHsv(rgb));
            }}
            className="h-8 w-20 px-2 text-sm font-mono uppercase text-center"
            maxLength={7}
          />
          <div className="text-center text-xs text-muted-foreground">HEX</div>
        </div>
      </div>
    </div>
  );
}
